import './PRMTable.scss';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import Button from '../../../../components/Shared/Button';
import Pagination from '../../../../components/DataTable/Pagination';
import { TABLE_IS_EMPTY, ITEMS_PER_PAGE, ACTIONS_LABEL } from './PRMTableConstants';
import type { CellValue, PRMTableProps, Rows, TableColumn } from './PRMTableType';

const ACTIONS_COLUMN_WIDTH = '80px';
type TableRow = Rows['data'][number];
type ChipSource = NonNullable<TableColumn['chips']>[number];

const getCommaSeparatedValues = (value: CellValue): string[] =>
  String(value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const renderChip = (row: TableRow, column: TableColumn, source: ChipSource, value: string, index: number) => {
  const chipKey = `${source.field}-${value}-${index}`;
  if (!source.status) {
    return (
      <span className="prmTable-chip" key={chipKey}>
        {value}
      </span>
    );
  }

  const chip = (
    <button type="button" className={`prmTable-chip prmTable-chip--${source.status}`} key={chipKey}>
      {value}
      <span className="visually-hidden"> ({source.status})</span>
    </button>
  );

  return (
    <OverlayTrigger
      key={chipKey}
      trigger={['hover', 'focus']}
      placement="top"
      overlay={
        <Tooltip id={`prmTable-status-${row.id}-${column.key}-${chipKey}`}>
          {source.status === 'active' ? 'Active' : 'Inactive'}
        </Tooltip>
      }
    >
      {chip}
    </OverlayTrigger>
  );
};

const renderSourceChips = (row: TableRow, column: TableColumn, source: ChipSource) =>
  getCommaSeparatedValues(row[source.field]).map((value, index) => renderChip(row, column, source, value, index));

const ChipCellContent = ({ row, column }: { row: TableRow; column: TableColumn }) => (
  <div className="prmTable-chips">{column.chips?.flatMap((source) => renderSourceChips(row, column, source))}</div>
);

const ShowMoreCellContent = ({
  row,
  column,
  isExpanded,
  onToggle,
}: {
  row: TableRow;
  column: TableColumn;
  isExpanded: boolean;
  onToggle: (cellKey: string) => void;
}) => {
  const brands = getCommaSeparatedValues(row[column.key]);
  const cellKey = JSON.stringify([row.id, column.key]);
  const maxVisibleItems = Math.max(0, column.showMore?.maxVisibleItems ?? 0);
  const hasHiddenItems = brands.length > maxVisibleItems;
  const visibleBrands = isExpanded ? brands : brands.slice(0, maxVisibleItems);

  return (
    <>
      <span className="prmTable-moreContent">{visibleBrands.join(', ')}</span>
      {hasHiddenItems && (
        <button
          type="button"
          className="prmTable-showMore"
          aria-expanded={isExpanded}
          onClick={() => onToggle(cellKey)}
        >
          {isExpanded ? 'Show less' : `Show more (${brands.length - maxVisibleItems})`}
        </button>
      )}
    </>
  );
};

const TableCellContent = ({
  row,
  column,
  expandedCells,
  onToggle,
}: {
  row: TableRow;
  column: TableColumn;
  expandedCells: Set<string>;
  onToggle: (cellKey: string) => void;
}) => {
  if (column.chips?.length) return <ChipCellContent row={row} column={column} />;

  if (column.showMore) {
    const cellKey = JSON.stringify([row.id, column.key]);
    return (
      <ShowMoreCellContent row={row} column={column} isExpanded={expandedCells.has(cellKey)} onToggle={onToggle} />
    );
  }

  return <>{row[column.key]}</>;
};

const PRMTable = ({ rows, columns, toolbar, paginationResetKey }: PRMTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedCells, setExpandedCells] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setCurrentPage(1);
  }, [paginationResetKey]);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentRows = rows.data.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  const hasActions = Boolean(rows.actions?.length);
  const toggleExpandedCell = (cellKey: string) => {
    setExpandedCells((current) => {
      const next = new Set(current);
      if (next.has(cellKey)) next.delete(cellKey);
      else next.add(cellKey);
      return next;
    });
  };

  return (
    <Row>
      <Col xs={12} sm={12} md={12}>
        <div className="prmTable">
          {toolbar}
          <table className="table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} scope="col" style={{ width: column.width }}>
                    {column.label}
                  </th>
                ))}
                {hasActions && (
                  <th className="prmTable-actionsColumn" style={{ width: ACTIONS_COLUMN_WIDTH }}>
                    {ACTIONS_LABEL}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.data.length === 0 && (
                <tr>
                  <td colSpan={columns.length + (hasActions ? 1 : 0)}>{TABLE_IS_EMPTY}</td>
                </tr>
              )}
              {currentRows.map((row) => (
                <tr key={row.id}>
                  {columns.map((column) => (
                    <td key={column.key} style={{ width: column.width }}>
                      <TableCellContent
                        row={row}
                        column={column}
                        expandedCells={expandedCells}
                        onToggle={toggleExpandedCell}
                      />
                    </td>
                  ))}
                  {hasActions && (
                    <td className="prmTable-actionsColumn" style={{ width: ACTIONS_COLUMN_WIDTH }}>
                      <div className="prmTable-actions">
                        {rows.actions?.map((action) => (
                          <Button
                            key={action.label}
                            className={`Button Button--round Button--small ${
                              action.icon === 'edit-write' ? 'Button--dawnPink mr-2' : 'Button--red'
                            } Button--title`}
                            data-title={action.label}
                            aria-label={action.label}
                            onClick={() => action.onClick(row)}
                          >
                            <i className={`icon-${action.icon}`} aria-hidden="true"></i>
                          </Button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {rows.data.length > ITEMS_PER_PAGE && (
            <Row className="prmTable-pagination">
              <Col xs={12} className="d-flex justify-content-center text-center">
                <Pagination
                  total={rows.data.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </Col>
            </Row>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default PRMTable;
